import { useState, type FormEvent } from "react";
import { useData } from "@/contexts/DataContext";
import { Plus, Pencil, Trash2, X, Image as ImageIcon, Search } from "lucide-react";
import { NewsItem } from "@/data/mockData";
import ImageUpload from "@/components/ImageUpload";
import RichTextEditor from "@/components/RichTextEditor";
import { toApiDate, toDisplayDateInput } from "@/lib/dateFormat";

const emptyNews: Omit<NewsItem, "id"> = {
  title: "",
  summary: "",
  content: "",
  contentAlignment: "justify",
  fontStyle: "times_new_roman",
  category: "English",
  date: toDisplayDateInput(new Date().toISOString().split("T")[0]),
  image: "",
  author: "",
  videoUrl: "",
};

const AdminNews = () => {
  const { news, addNews, updateNews, deleteNews } = useData();
  const [editing, setEditing] = useState<NewsItem | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>("");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Only allow English and Telugu categories
  const restrictedCategories = ["English", "Telugu"];
  const contentAlignmentOptions: Array<{ value: NewsItem["contentAlignment"]; label: string }> = [
    { value: "justify", label: "Justified" },
    { value: "left", label: "Left" },
    { value: "center", label: "Center" },
    { value: "right", label: "Right" },
  ];
  const fontStyleOptions: Array<{ value: NewsItem["fontStyle"]; label: string }> = [
    { value: "normal", label: "Normal" },
    { value: "times_new_roman", label: "Times New Roman" },
    { value: "courier_new", label: "Courier New" },
  ];

  const openNew = () => {
    setSaveError("");
    setEditing({ id: Date.now().toString(), ...emptyNews });
    setIsNew(true);
  };
  const openEdit = (item: NewsItem) => {
    setSaveError("");
    setEditing({ ...item, date: toDisplayDateInput(item.date) });
    setIsNew(false);
  };
  const handleSave = async () => {
    if (!editing) return;

    setIsSaving(true);
    setSaveError("");

    try {
      if (isNew) {
        await addNews(editing);
      } else {
        await updateNews(editing.id, editing);
      }

      setEditing(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to save article";
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = (id: string) => { if (confirm("Delete this article?")) deleteNews(id); };
  const update = (field: keyof NewsItem, value: string) => setEditing((prev) => prev ? { ...prev, [field]: value } : null);
  const onSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const toSortableTime = (value: string) => {
    const normalized = toApiDate(value);
    const timestamp = Date.parse(normalized || "");
    return Number.isFinite(timestamp) ? timestamp : 0;
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const sortedNews = [...news].sort((a, b) => toSortableTime(b.date) - toSortableTime(a.date));
  const filteredNews = !normalizedSearch
    ? sortedNews
    : sortedNews.filter((item) =>
        [
          item.title,
          item.category,
          item.author,
          item.summary,
          item.content,
          item.date,
          toDisplayDateInput(item.date),
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalizedSearch)
      );

  const totalPages = Math.max(1, Math.ceil(filteredNews.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedNews = filteredNews.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold text-foreground">Manage News</h1>
        <button onClick={openNew} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium">
          <Plus size={16} /> Add Article
        </button>
      </div>
      <form onSubmit={onSearchSubmit} className="mb-6 flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search by title, category, author, date..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-input bg-background text-sm"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
        >
          <Search size={15} />
          Search
        </button>
      </form>

      <div className="finance-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-3 font-semibold">Image</th>
                <th className="text-left px-4 py-3 font-semibold">Title</th>
                <th className="text-left px-4 py-3 font-semibold">Category</th>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Author</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedNews.map((item) => (
                <tr key={item.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-3">
                    {item.image ? (
                      <img 
                        src={item.image} 
                        alt={item.title}
                        className="w-12 h-12 object-cover rounded-lg"
                        onError={(e) => {
                          e.currentTarget.src = "/placeholder.svg";
                        }}
                      />
                    ) : (
                      <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                        <ImageIcon size={16} className="text-muted-foreground" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium max-w-xs truncate">{item.title}</td>
                  <td className="px-4 py-3"><span className="badge-info">{item.category}</span></td>
                  <td className="px-4 py-3 text-muted-foreground">{item.date}</td>
                  <td className="px-4 py-3 text-muted-foreground">{item.author}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(item)} className="p-1.5 rounded hover:bg-muted"><Pencil size={15} /></button>
                      <button onClick={() => handleDelete(item.id)} className="p-1.5 rounded hover:bg-destructive/10 text-destructive"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {filteredNews.length === 0 && (
        <div className="text-center py-6 text-sm text-muted-foreground">No news items found.</div>
      )}
      {filteredNews.length > 0 && (
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-muted-foreground">
            Showing {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, filteredNews.length)} of {filteredNews.length}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
              disabled={safePage === 1}
              className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1.5 rounded-lg text-xs border ${
                  safePage === page ? "bg-primary text-primary-foreground border-primary" : "border-input"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))}
              disabled={safePage === totalPages}
              className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-card rounded-xl border border-border p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-display font-bold">{isNew ? "Add Article" : "Edit Article"}</h2>
              <button onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Title</label>
                <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.title} onChange={(e) => update("title", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Summary</label>
                <div className="mt-1">
                  <RichTextEditor
                    value={editing.summary}
                    onChange={(value) => update("summary", value)}
                    placeholder="Write a rich summary. Select text and apply Normal, Bold, H2, Link, or Undo."
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Content Alignment</label>
                  <select
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm"
                    value={editing.contentAlignment}
                    onChange={(e) => update("contentAlignment", e.target.value)}
                  >
                    {contentAlignmentOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Font Style</label>
                  <select
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm"
                    value={editing.fontStyle}
                    onChange={(e) => update("fontStyle", e.target.value)}
                  >
                    {fontStyleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {saveError && <p className="text-sm text-destructive">{saveError}</p>}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Category</label>
                  <select className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.category} onChange={(e) => update("category", e.target.value)}>
                    {restrictedCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Date</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm"
                    value={editing.date}
                    onChange={(e) => update("date", e.target.value)}
                    placeholder="dd-mm-yyyy"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Author</label>
                  <input className="w-full mt-1 px-3 py-2 rounded-lg border border-input bg-background text-sm" value={editing.author} onChange={(e) => update("author", e.target.value)} />
                </div>
              </div>
              <div className="mt-4">
                <ImageUpload 
                  value={editing.image} 
                  onChange={(url) => update("image", url)}
                  placeholder="Upload news image or enter URL"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={handleSave} disabled={isSaving} className="flex-1 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-60">
                {isSaving ? "Saving..." : "Save"}
              </button>
              <button onClick={() => setEditing(null)} className="flex-1 py-2.5 rounded-lg bg-muted text-foreground font-semibold text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNews;

